import { formatValue, humanizeKey } from '../components/DataPointsTable';
import { META_KEY, accountNumberOf, institutionOf, matchKeyOf, titleOf } from './clientReport';

/**
 * Search within one client's eMoney report: "visa", "1234", "chase savings",
 * "cash balance". Every word must appear somewhere in an account (its
 * section, document type, institution, name, account number, source file,
 * field names or values); documents without data match on their section,
 * type and file name.
 */

/** eMoney field names used as keys ("Current Balance") show as written; snake_case is humanized. */
export const fieldLabel = (key) => (/[\sA-Z]/.test(String(key)) ? String(key) : humanizeKey(key));

/** Lowercase words of a query; empty when there is nothing to search for. */
export const queryTokens = (query) => String(query || '').toLowerCase().split(/\s+/).filter(Boolean);

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

// The text a value can be found by: as written and as displayed
// (5230.55 and "5,230.55").
const valueTexts = (value) => {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) return value.flatMap(valueTexts);
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, v]) => [fieldLabel(key), ...valueTexts(v)]);
  }
  const shown = formatValue(value);
  return shown === String(value) ? [shown] : [String(value), shown];
};

const fieldTexts = (fields) => Object.entries(fields || {})
  .filter(([key]) => key !== META_KEY)
  .flatMap(([key, value]) => [fieldLabel(key), ...valueTexts(value)]);

const docTexts = (doc) => [doc.docTypeName, doc.subTypeName, doc.documentName];

const accountText = (set, section, account) => {
  const { fields, doc } = account.current;
  return [
    set.label,
    section.label,
    ...docTexts(doc),
    ...account.older.map((entry) => entry.doc.documentName),
    institutionOf(fields),
    titleOf(fields),
    matchKeyOf(fields),
    accountNumberOf(fields),
    ...fieldTexts(fields),
  ].filter((part) => part !== null && part !== undefined).join('\n').toLowerCase();
};

const matchesAll = (text, tokens) => tokens.every((token) => text.includes(token));

/**
 * report: buildClientReport output. Returns null for an empty query, else
 * { tokens, accounts: Set<account key>, docs: Set<fileId of documents
 * without data>, count }.
 */
export const searchReport = (report, query) => {
  const tokens = queryTokens(query);
  if (!tokens.length) return null;
  const accounts = new Set();
  const docs = new Set();
  (report || []).forEach((set) => set.sections.forEach((section) => {
    section.accounts.forEach((account) => {
      if (matchesAll(accountText(set, section, account), tokens)) accounts.add(account.key);
    });
    section.withoutData.forEach((doc) => {
      const text = [set.label, section.label, ...docTexts(doc)].filter(Boolean).join('\n').toLowerCase();
      if (matchesAll(text, tokens)) docs.add(doc.fileId);
    });
  }));
  return { tokens, accounts, docs, count: accounts.size + docs.size };
};

/** The report narrowed to a search result's matches; empty sections and sets drop out. */
export const filterReport = (report, result) => {
  if (!result) return report;
  return (report || [])
    .map((set) => ({
      ...set,
      sections: set.sections
        .map((section) => ({
          ...section,
          accounts: section.accounts.filter((account) => result.accounts.has(account.key)),
          withoutData: section.withoutData.filter((doc) => result.docs.has(doc.fileId)),
        }))
        .filter((section) => section.accounts.length || section.withoutData.length),
    }))
    .filter((set) => set.sections.length);
};
